import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import PageHeader from "../../components/PageHeader";
import api, { fmtErr, fmtMoney, toastErr } from "../../api";
import { QuoteActions } from "./QuoteActions";
import { toast } from "sonner";

const STATUSES = ["draft", "sent", "viewed", "accepted", "declined", "expired", "withdrawn"];
const TYPES = [["", "All"], ["one_time", "One-time"], ["seasonal", "Seasonal"]];

export default function Quotes() {
  const nav = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState("");
  const [year, setYear] = useState("");
  const [qtype, setQtype] = useState("");
  const load = () => {
    const params = {};
    if (status) params.status = status;
    if (year) params.year = year;
    if (qtype) params.quote_type = qtype;
    api.get("/quotes", { params }).then((r) => setQuotes(r.data)).catch((e) => toastErr(e));
    api.get("/quotes/summary").then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(load, [status, year, qtype]);

  const dup = async (id) => {
    try {
      const r = await api.post(`/quotes/${id}/duplicate`);
      toast.success(`Duplicated as ${r.data.quote_number}`);
      nav(`/quotes/${r.data.id}`);
    } catch (e) { toastErr(e); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this draft?")) return;
    try { await api.delete(`/quotes/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toastErr(e); }
  };

  const attention = new Set(summary?.attention || []);
  return (
    <Layout>
      <PageHeader title="Service quotes" back="/quotes" />
      <div className="mb-3 flex gap-2" data-testid="type-filters">
        {TYPES.map(([k, lbl]) => (
          <button key={k} data-testid={`filter-type-${k || "all"}`} onClick={() => setQtype(k)}
            className={`h-10 flex-1 rounded-full border px-4 text-xs font-bold transition-colors ${qtype === k ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`}>
            {lbl}
          </button>
        ))}
      </div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex gap-2">
          <select data-testid="filter-status" className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input data-testid="filter-year" className="field-input w-24" placeholder="Year" value={year} onChange={(e) => setYear(e.target.value)} />
        </div>
        <Link to="/quotes/new" data-testid="new-quote-btn"
          className="rounded-md bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground">New quote</Link>
      </div>
      {summary && (
        <div className="card-flat mb-4 grid grid-cols-4 gap-2 p-4 text-center" data-testid="quote-stats">
          <div><p className="text-lg font-bold">{summary.sent}</p><p className="text-xs text-muted-foreground">Sent</p></div>
          <div><p className="text-lg font-bold">{summary.accepted}</p><p className="text-xs text-muted-foreground">Accepted</p></div>
          <div><p className="text-lg font-bold">{summary.win_rate}%</p><p className="text-xs text-muted-foreground">Win rate</p></div>
          <div><p className="text-lg font-bold">{fmtMoney(summary.value_accepted)}</p><p className="text-xs text-muted-foreground">Accepted value</p></div>
        </div>
      )}
      <div className="space-y-2" data-testid="quotes-list">
        {quotes.map((q) => (
          <div key={q.id} data-testid={`quote-row-${q.quote_number}`}
            className={`card-flat p-4 ${attention.has(q.id) ? "border-2 border-amber-400" : ""}`}>
            <Link to={`/quotes/${q.id}`} data-testid={`quote-open-${q.quote_number}`}>
              <div className="flex items-baseline justify-between gap-2">
                <p className="font-bold">{q.quote_number}
                  <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-xs font-bold text-secondary-foreground" data-testid={`quote-type-badge-${q.quote_number}`}>
                    {q.quote_type === "one_time" ? "One-time" : "Seasonal"}
                  </span>
                  {q.revision > 1 && <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-xs font-bold" data-testid={`quote-rev-badge-${q.quote_number}`}>Rev {q.revision}</span>}
                  {q.status === "accepted" && !q.converted_agreement_id && !q.converted_visit_id && (
                    <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-700" data-testid={`quote-not-converted-${q.quote_number}`}>Accepted — not converted</span>
                  )}
                  <span className="ml-1 font-normal text-muted-foreground">· {q.contact_name}{q.company_name ? ` (${q.company_name})` : ""}</span>
                </p>
                <p className="text-sm font-semibold capitalize">{q.status}</p>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {q.quote_type === "one_time" ? (
                  <>{q.title || "One-time job"} · <span className="font-semibold text-foreground" data-testid={`quote-total-${q.quote_number}`}>{fmtMoney(q.price_totals?.total ?? 0)}</span></>
                ) : (
                  <>
                    {q.season_year} · {q.visit_count || "—"} {q.frequency} visits
                    {q.accepted_option ? ` · Accepted ${q.accepted_option} ${fmtMoney(q.totals?.[q.accepted_option])}`
                      : ` · ${(q.options || []).map((o) => `${o.label} ${fmtMoney(q.totals?.[o.label])}`).join(" / ") || "no options"}`}
                  </>
                )}
                {" · valid to "}{q.valid_until}
                {attention.has(q.id) && <span className="ml-2 font-semibold text-amber-600">needs follow-up</span>}
              </p>
            </Link>
            <div className="mt-2" data-testid={`quote-actions-row-${q.quote_number}`}>
              <QuoteActions q={q} onChanged={() => load()} />
            </div>
            <div className="mt-2 flex gap-2">
              <button data-testid={`quote-dup-${q.quote_number}`} onClick={() => dup(q.id)} className="text-xs font-bold text-primary">Duplicate</button>
              {q.status === "draft" && (
                <button data-testid={`quote-del-${q.quote_number}`} onClick={() => del(q.id)} className="text-xs font-bold text-red-600">Delete</button>
              )}
            </div>
          </div>
        ))}
        {!quotes.length && <p className="text-sm text-muted-foreground" data-testid="quotes-empty">No quotes yet — create one from a lead, an owner, or here.</p>}
      </div>
    </Layout>
  );
}
